import RefreshIcon from 'components/ReusableComponents/RefreshIcon'
import NewRejectDialog from 'components/ReusableComponents/RejectDialog'
import BookingHistory from 'features/BookingHistory'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { connect, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { Link, Redirect, useParams } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import billingApi from 'services/api/billing'
import dashboardApi from 'services/api/dashboard'
import invoiceApi from 'services/api/invoices'

import { selectCurrentHotelId } from '../../accessors/index'
import { cleanPostedBlacklist, postBlacklist } from '../../actions/blacklist'
import {
  getBillableBookings,
  selectBillableBookings,
  updateBillableBooking
} from '../../actions/invoices'
import BillingContactsDialog from '../../components/ReusableComponents/BillingContactsDialog/index'
import BlacklistDialog from '../../components/ReusableComponents/BlacklistDialog/index'
import CustomHeader from '../../components/ReusableComponents/HeaderTitles/index'
import LoadingIndicator from '../../components/ReusableComponents/LoadingIndicator'
import RegularToggle from '../../components/ReusableComponents/Toggle/index'
import styles from './BillingManagement.module.scss'
import BottomBar from './BottomBar/index'
import Card from './Card'
import FilterBar from './FilterBar/index'
import getFilteredBookings from './getFilteredBookings'
import { AcceptDialog } from './ReasonDialogs'
import SelectedBookingsBar, { SelectAllBar } from './SelectedBookingsBar/index'
import TopBar from './TopBar/index'
import useBillingManagementFilters from './useBillingManagementFilters'
import { ReactComponent as ViewIcon } from './view.svg'
import EditWarningDialog from './Warnings/Edit/index'
import SendWarningDialog from './Warnings/Send/index'

const BillableBookings = ({
  billableSubsidiaryName,
  endDate,
  billableBookings,
  dispatch,
  invoiceStatus,
  deliveredByName,
  deliveredOn,
  isLoading,
  isErrored,
  isPostingBlacklist,
  isBlacklistSubmitted,
  selectedBookings,
  errorMessage
}) => {
  const { addToast } = useToasts()

  const { hotelId, invoiceId } = useParams()

  const history = useHistory()

  const [isRejectDialogVisible, setIsRejectDialogVisible] = useState(false)
  const [isAcceptDialogVisible, setIsAcceptDialogVisible] = useState(false)
  const [modifiedBookings, setModifiedBookings] = useState()
  const [completingReview, setCompletingReview] = useState(false)
  const [isSkippingMonth, setIsSkippingMonth] = useState()
  const [isBlacklistDialogOpen, setBlacklistDialogOpen] = useState(false)
  const [conversionToBeBlacklisted, setConversionToBeBlacklisted] = useState()
  const [isEditMode, setIsEditMode] = useState(true)
  const [isEditWarningVisible, setEditWarningVisible] = useState(false)
  const [isSendWarningVisible, setSendWarningVisible] = useState(false)
  const [billingContacts, setBillingContacts] = useState([])
  const [isContactsDialogVisible, setContactsDialogVisible] = useState(false)
  const [filteredBookings, setFilteredBookings] = useState([])
  const [excludeTempImmunes, setExcludeTempImmunes] = useState(false)
  const [lastResendReason, setLastResendReason] = useState()

  const [guestDataToInspect, setGuestDataToInspect] = useState(null)

  const currentHotelId = useSelector(selectCurrentHotelId)

  useEffect(() => {
    dispatch(getBillableBookings(invoiceId, currentHotelId))
  }, [currentHotelId, dispatch, invoiceId])

  const handleResendInvoiceClick = async reason => {
    setLastResendReason(reason)
    handleSendInvoiceClick()
  }

  const handleSendInvoiceClick = async () => {
    if (invoiceStatus === 'PaymentPending' && !isSendWarningVisible) {
      setSendWarningVisible(true)
      return
    }

    if (invoiceStatus === 'PaymentPending' && isSendWarningVisible) {
      setSendWarningVisible(false)
    }

    if (!isContactsDialogVisible) {
      if (!billingContacts.length) {
        dashboardApi.getBillingContacts().then(data => {
          setBillingContacts(data)
          setContactsDialogVisible(true)
        })
      } else {
        setContactsDialogVisible(true)
      }
    }
  }

  const handleReviewComplete = async () => {
    setCompletingReview(true)
    await dashboardApi.invoiceReviewComplete(invoiceId).then(() => {
      setCompletingReview(false)
      dispatch(getBillableBookings(invoiceId, currentHotelId))
      setContactsDialogVisible(false)
      setLastResendReason(null)
    })
  }

  const skipMonth = async () => {
    setIsSkippingMonth(true)
    invoiceApi
      .skipMonth({ invoiceId })
      .then(() => {
        history.push(`/invoices`)
      })
      .catch(err => {
        addToast(`Couldn't skip month: ${err.toString}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsSkippingMonth(false)
      })
  }

  const handleSendInvoice = async recipients => {
    const res = await dashboardApi.sendInvoice(invoiceId, recipients, lastResendReason)

    if (res.isSuccess) {
      dispatch(getBillableBookings(invoiceId, currentHotelId))
      setContactsDialogVisible(false)
      setLastResendReason(null)
    }
  }

  const hideBlacklistDialog = () => {
    setBlacklistDialogOpen(false)
    setConversionToBeBlacklisted(null)
  }

  const openBlacklistDialog = booking => {
    setBlacklistDialogOpen(true)
    setConversionToBeBlacklisted(booking)
  }

  const blackListDirect = async exclusions => {
    const res = await dashboardApi.addDirectBlacklist(exclusions)

    if (res.isSuccess) {
      addToast(`Added ${exclusions.length} exclusions`, {
        appearance: 'success',
        autoDismiss: true
      })
    } else {
      addToast(`Error during update: ${res.message}`, {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  const blacklistBookings = (exclusions, convertedSubscriberHotelBookingId) => {
    dispatch(postBlacklist(exclusions, convertedSubscriberHotelBookingId))
  }

  // end of blacklist

  const handleChangeBookingState = (ids, willCharge) => {
    setModifiedBookings(ids)

    if (willCharge) {
      setIsAcceptDialogVisible(true)
    } else {
      setIsRejectDialogVisible(true)
    }
  }

  // submit reject(?)

  const rejectBookings = async bookingsToReject => {
    const bookingIdsToReject = bookingsToReject.map(
      b => b.inviteBookingConversionDetailId
    )

    const response = await dashboardApi.postChangedConversation({
      invoiceId,
      conversionModifyRequests: bookingIdsToReject.map(id => ({
        inviteBookingConversionDetailId: id,
        isExcluded: true,
        exclusionReason: 'notEnoughInfo'
      }))
    })

    if (response.isSuccess) {
      const { modifiedConversions, invoice } = response.result

      setExcludeTempImmunes(true)

      modifiedConversions.forEach(booking => {
        dispatch(updateBillableBooking(booking, invoice))
      })
    } else {
      addToast(`Error during update: ${response.message}`, {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  const handleSubmitRejectDialog = async exclusionReason => {
    const response = await dashboardApi.postChangedConversation({
      invoiceId,
      conversionModifyRequests: modifiedBookings.map(id => ({
        inviteBookingConversionDetailId: id,
        isExcluded: isRejectDialogVisible ? true : false,
        exclusionReason
      }))
    })

    setIsAcceptDialogVisible(false)
    setIsRejectDialogVisible(false)

    if (response.isSuccess) {
      const { modifiedConversions, invoice } = response.result

      setExcludeTempImmunes(true)

      modifiedConversions.forEach(booking => {
        dispatch(updateBillableBooking(booking, invoice))
      })
    } else {
      addToast(`Error during update: ${response.message}`, {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  // end of submit reject

  const handleSelectAll = () => {
    dispatch(
      selectBillableBookings(
        filteredBookings.map(b => b.convertedSubscriberHotelBookingId)
      )
    )
  }

  const handleMultiSelectClick = ({ convertedSubscriberHotelBookingId }) => {
    dispatch(selectBillableBookings([convertedSubscriberHotelBookingId]))
  }

  const handleChangeEditMode = () => {
    if (invoiceStatus === 'PaymentPending') {
      setEditWarningVisible(true)
    } else {
      setIsEditMode(!isEditMode)
    }
  }

  const handleSubmitEditWarning = () => {
    setEditWarningVisible(false)
    setIsEditMode(true)
  }

  useEffect(() => {
    if (isBlacklistSubmitted) {
      dispatch(cleanPostedBlacklist())
      setBlacklistDialogOpen(false)
      setConversionToBeBlacklisted(null)
      addToast(`Exclusion saved successfully! Results will be updated in a few minutes`, {
        appearance: 'success',
        autoDismiss: true,
        pauseOnHover: true
      })
    }
  }, [addToast, dispatch, isBlacklistSubmitted])

  useEffect(() => {
    if (['PaymentPending', 'Paid'].includes(invoiceStatus)) {
      setIsEditMode(false)
    } else {
      setIsEditMode(true)
    }
  }, [invoiceStatus])

  const [filterVals, setFilterVal] = useBillingManagementFilters()

  useEffect(() => {
    if (billableBookings && billableBookings.length > 0) {
      const filtered = getFilteredBookings(
        billableBookings,
        excludeTempImmunes,
        filterVals
      )
      setFilteredBookings([...filtered])
    }
  }, [billableBookings, excludeTempImmunes, filterVals])

  useEffect(() => {
    setExcludeTempImmunes(false)
  }, [filterVals.showRejected, filterVals.showNonRejected])

  const [pendingReconciliationCount, setPendingReconciliationCount] = useState({
    curr: 0,
    prev: 0
  })

  useEffect(() => {
    const calculatedLength = billableBookings.filter(
      b => b.lastReconciliationStatus === 'Pending'
    ).length

    setPendingReconciliationCount(lastState => ({
      curr: calculatedLength,
      prev: calculatedLength > lastState.prev ? calculatedLength : lastState.prev
    }))
  }, [billableBookings, invoiceId])

  useEffect(() => {
    if (pendingReconciliationCount.curr === 0 && pendingReconciliationCount.prev > 0) {
      billingApi
        .emailReconciledInvoices({ invoiceId })
        .then(() => {
          addToast(`Sent updated invoice email the customer`, {
            appearance: 'success',
            autoDismiss: true
          })
        })
        .catch(err => {
          addToast(
            `Failed to send updated invoice email the customer: ${err.toString()}`,
            {
              appearance: 'error',
              autoDismiss: true
            }
          )
        })
    }
  }, [pendingReconciliationCount, addToast, invoiceId])

  return (
    <>
      {currentHotelId !== hotelId && <Redirect to="/invoices"></Redirect>}
      {isLoading && <LoadingIndicator />}
      {guestDataToInspect && (
        <BookingHistory
          bookingId={guestDataToInspect.bookingId}
          beforeDate={guestDataToInspect?.beforeDate}
          userData={guestDataToInspect.userData}
          handleClose={() => setGuestDataToInspect(null)}
        />
      )}
      {isBlacklistDialogOpen && (
        <BlacklistDialog
          conversion={conversionToBeBlacklisted}
          handleHideDialog={hideBlacklistDialog}
          handleSubmit={blacklistBookings}
          isLoading={isPostingBlacklist}
        />
      )}
      {isErrored && (
        <div className={styles.error}>
          <div className={styles.errorTitle}>Something went wrong</div>
          <div className={styles.errorDesc}>{errorMessage.toString()}</div>
          <Link to="/billing">
            <button className={styles.noResultsButton}>Go back to invoices</button>
          </Link>
        </div>
      )}

      {isRejectDialogVisible && (
        <NewRejectDialog
          onHide={() => setIsRejectDialogVisible(false)}
          onRejectBookings={rejectBookings}
          onBlacklistBookings={blackListDirect}
          isMultiWithSameRate={filterVals.rateToFilter !== 'any'}
          isMultiWithSameTracking={filterVals.trackingToFilter !== 'any'}
          affectedBookings={billableBookings.filter(b =>
            modifiedBookings.some(id => b.inviteBookingConversionDetailId === id)
          )}
        />
      )}
      {isAcceptDialogVisible && (
        <AcceptDialog
          handleHideDialog={() => setIsAcceptDialogVisible(false)}
          handleConfirm={handleSubmitRejectDialog}
          multiBookings={modifiedBookings.length > 1}
        />
      )}
      {isContactsDialogVisible && (
        <BillingContactsDialog
          contacts={billingContacts}
          handleHideDialog={() => setContactsDialogVisible(false)}
          handleConfirm={handleSendInvoice}
        />
      )}
      {isEditWarningVisible && (
        <EditWarningDialog
          sentDate={deliveredOn}
          sendBy={deliveredByName}
          onConfirm={handleSubmitEditWarning}
          onClose={() => setEditWarningVisible(false)}
        />
      )}
      {isSendWarningVisible && (
        <SendWarningDialog
          sentDate={deliveredOn}
          sendBy={deliveredByName}
          handleConfirm={handleResendInvoiceClick}
          handleHideDialog={() => setSendWarningVisible(false)}
        />
      )}
      {!isLoading && billableBookings.length > 0 ? (
        <div className={styles.container}>
          <CustomHeader width={1100} isFixed={true}>
            <div className={styles.header}>
              <h1 className={styles.headerTitle}>
                <strong>{moment(endDate).format('MMMM')}</strong> bookings of{' '}
                {billableSubsidiaryName}
              </h1>
              <p className={styles.headerDesc}>
                {filteredBookings.length < billableBookings.length
                  ? `Showing ${filteredBookings.length} of ${billableBookings.length} bookings`
                  : `Showing ${filteredBookings.length} bookings`}{' '}
                / Invoice #{invoiceId}
              </p>
            </div>
          </CustomHeader>
          <TopBar>
            <>
              <FilterBar
                setFilterVal={setFilterVal}
                filterVals={filterVals}
                billableBookings={billableBookings}
              />
              <div className={styles.topBarExtras}>
                {invoiceStatus === 'Paid' ? (
                  <div className={styles.viewOnly}>
                    <ViewIcon />
                    View only
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() =>
                        dispatch(getBillableBookings(invoiceId, currentHotelId))
                      }
                    >
                      <RefreshIcon className={styles.refreshIcon} fill="blue" />
                    </button>
                    <RegularToggle
                      isGray={true}
                      className={styles.editModeToggle}
                      checked={isEditMode}
                      label="Edit mode"
                      labelClass={styles.editModeLabel}
                      handleChange={handleChangeEditMode}
                    />
                  </>
                )}
              </div>
            </>
          </TopBar>
          {selectedBookings.length > 0 && (
            <SelectedBookingsBar handleChangeBookingState={handleChangeBookingState} />
          )}
          {selectedBookings.length === 0 &&
            filteredBookings.length > 0 &&
            (filterVals.rateToFilter !== 'any' ||
              filterVals.trackingToFilter !== 'any' ||
              filterVals.priorStayCount !== 'Any prior stays' ||
              filterVals.showAnomaly) &&
            isEditMode && (
              <SelectAllBar
                count={filteredBookings.length}
                handleSelectAll={handleSelectAll}
              />
            )}
          <div className={styles.cardHolder}>
            {filteredBookings.length > 0
              ? filteredBookings
                  .sort(a => (a.lastReconciliationStatus === 'Pending' ? -1 : 1))
                  .map(b => (
                    <Card
                      {...b}
                      handleCheck={() =>
                        handleChangeBookingState(
                          [b.inviteBookingConversionDetailId],
                          b.isInvoicingRejected
                        )
                      }
                      reloadResults={() =>
                        dispatch(getBillableBookings(invoiceId, currentHotelId))
                      }
                      handleBlacklistClick={() => openBlacklistDialog(b)}
                      handleMultiSelectClick={() => handleMultiSelectClick(b)}
                      isEditable={isEditMode}
                      setGuestDataToInspect={setGuestDataToInspect}
                    />
                  ))
              : 'no results'}
          </div>
          <BottomBar
            handleReviewComplete={handleReviewComplete}
            completingReview={completingReview}
            skipMonth={skipMonth}
            isSkippingMonth={isSkippingMonth}
          />
        </div>
      ) : (
        <>
          {!isLoading && !isErrored && (
            <div>
              <CustomHeader width={1100} isFixed={true}>
                <div className={styles.header}>
                  <h1 className={styles.headerTitle}>
                    <strong>{moment(endDate).format('MMMM')}</strong> bookings of{' '}
                    {billableSubsidiaryName}
                  </h1>
                </div>
              </CustomHeader>
              <h1 className={styles.noResults}>
                No bookings found for {moment(endDate).format('MMMM')} period
              </h1>
              <Link to="/billing">
                <button className={styles.noResultsButton}>Go back to invoices</button>
              </Link>
              <BottomBar
                skipMonth={skipMonth}
                isSkippingMonth={isSkippingMonth}
                handleReviewComplete={handleReviewComplete}
                completingReview={completingReview}
              />
            </div>
          )}
        </>
      )}
    </>
  )
}

const mapStateToProps = ({ invoices, login, blacklist }) => ({
  billableBookings: invoices.billableBookings,
  endDate: invoices.endDate,
  billableSubsidiaryName: invoices.billableSubsidiaryName,
  currentHotel: login.currentHotel,
  invoiceStatus:
    invoices.billableInvoiceDetails && invoices.billableInvoiceDetails.status,
  deliveredByName:
    invoices.billableInvoiceDetails && invoices.billableInvoiceDetails.deliveredByName,
  deliveredOn:
    invoices.billableInvoiceDetails && invoices.billableInvoiceDetails.deliveredOn,
  isLoading: invoices.billableBookingsLoading,
  isErrored: invoices.billableBookingsFailed,
  errorMessage: invoices.billableBookingsError,
  isPostingBlacklist: blacklist.isPosting,
  isBlacklistSubmitted: blacklist.blacklistSubmitted,
  updatedOn: invoices.updatedOn,
  selectedBookings: invoices.selectedBookings
})

export default connect(mapStateToProps)(BillableBookings)
